import React, { useState, useRef, useEffect } from 'react';
import { Button, ExpandMore, UtilityText } from '@policygenius/mortar';
import {
  DesktopFilterDropDown,
  FilterContainer,
  Reset,
  Status,
  FilterCheckBox,
  ResetButton,
} from './styles';
import { PRODUCT_TYPE_FILTER_OPTIONS, STATUS_FILTER_OPTIONS } from '../constants';
import { useReportContext } from 'context/ReportContext';
import { Props } from './types';

export const DesktopStatusFilter: React.FC<Props> = ({ reset, showProductType }) => {
  const { filter, setFilter } = useReportContext();
  const [showDropDown, setShowDropdown] = useState(false);

  const updateFilterSelections = (filterType: string, option: string, checked: boolean) => {
    reset();
    // if the option is already checked when the user clicks the checkbox,
    // that means it should be removed from filter state so it becomes unchecked
    if (checked) {
      const newFilter = filter.filter((object) => object[filterType] !== option);

      setFilter(newFilter);
    } else {
      const newFilter = { [filterType]: option };

      setFilter([...filter, newFilter]);
    }
  };

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dropdownCloser = (event: Event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        document.getElementById('dropdownButton') !== event.target
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', dropdownCloser);

    return () => document.removeEventListener('mousedown', dropdownCloser);
  }, [showDropDown]);

  const filterCountNumber = () => {
    const count = filter.length;

    return count === 0 ? '' : `(${count})`;
  };

  const filterText = showProductType ? 'Filter' : 'Status';

  return (
    <Status>
      <Button variant="filled" id="dropdownButton" onClick={() => setShowDropdown(!showDropDown)}>
        <span>
          {filterText} {filterCountNumber()}
        </span>
        <ExpandMore size="small" />
      </Button>
      {filter.length > 0 && (
        <ResetButton
          variant="quaternary"
          size="medium"
          onClick={() => {
            setFilter([]);
            reset();
          }}
        >
          Reset
        </ResetButton>
      )}
      {showDropDown && (
        <FilterContainer ref={dropdownRef}>
          <DesktopFilterDropDown>
            {showProductType && (
              <>
                <UtilityText size="medium" weight="bold">
                  Product Type
                </UtilityText>
                {PRODUCT_TYPE_FILTER_OPTIONS.map((option) => {
                  return (
                    <FilterCheckBox
                      name={option.value}
                      label={option.label}
                      value={option.value}
                      helpText={option.helpText}
                      checked={!!filter.find((item) => item.productType === option.value)}
                      key={option.value}
                      onChange={() => {
                        updateFilterSelections(
                          'productType',
                          option.value,
                          !!filter.find((item) => item.productType === option.value)
                        );
                      }}
                    />
                  );
                })}
                <UtilityText size="medium" weight="bold">
                  Status
                </UtilityText>
              </>
            )}
            {STATUS_FILTER_OPTIONS.map((option) => {
              return (
                <FilterCheckBox
                  name={option.value}
                  label={option.label}
                  value={option.value}
                  helpText={option.helpText}
                  checked={!!filter.find((item) => item.applicationStatus === option.value)}
                  key={option.value}
                  onChange={() => {
                    updateFilterSelections(
                      'applicationStatus',
                      option.value,
                      !!filter.find((item) => item.applicationStatus === option.value)
                    );
                  }}
                />
              );
            })}
          </DesktopFilterDropDown>
          <Reset>
            <Button
              variant="quaternary"
              size="medium"
              disabled={filter.length === 0}
              onClick={() => {
                setFilter([]);
                setShowDropdown(false);
              }}
            >
              Reset
            </Button>
          </Reset>
        </FilterContainer>
      )}
    </Status>
  );
};
